import React, { useEffect, useState } from "react";
import { Container, Card, Button, Spinner, Alert } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaClock } from "react-icons/fa";
import api from "../../utils/api";

const DepositStatus = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState("loading"); // loading, success, failed, pending
    const [depositData, setDepositData] = useState(null);

    useEffect(() => {
        const verifyDeposit = async () => {
            if (!orderId) {
                setStatus("failed");
                return;
            }

            try {
                // Poll backend or wait for webhook processing
                // For better UX, we call our backend to check the latest status of this order
                const res = await api.get(`/deposits/status/${orderId}`);
                if (res.data.success) {
                    setDepositData(res.data.deposit);
                    setStatus(res.data.deposit.status);
                } else {
                    setStatus("failed");
                }
            } catch (error) {
                console.error("Error verifying deposit:", error);
                setStatus("failed");
            }
        };

        verifyDeposit();
        
        // Optional: setup a small interval to poll if it's still pending
        const interval = setInterval(async () => {
            if (status === "pending" || status === "loading") {
                verifyDeposit();
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [orderId, status]);

    const renderContent = () => {
        switch (status) {
            case "loading":
                return (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="warning" className="mb-3" />
                        <h4>Verifying Payment...</h4>
                        <p className="text-muted">Please do not close or refresh this page.</p>
                    </div>
                );
            case "success":
                return (
                    <div className="text-center py-5">
                        <FaCheckCircle className="text-success mb-4" size={80} />
                        <h2 className="text-success fw-bold">Deposit Successful!</h2>
                        <p className="lead mb-4">
                            ₹{depositData?.amount} has been added to your wallet.
                        </p>
                        <div className="d-grid gap-2 d-sm-flex justify-content-sm-center">
                            <Button variant="warning" size="lg" className="fw-bold px-4" onClick={() => navigate("/user-dashboard")}>
                                Go to Dashboard
                            </Button>
                            <Button variant="outline-light" size="lg" className="px-4" onClick={() => navigate("/transactions")}>
                                View History
                            </Button>
                        </div>
                    </div>
                );
            case "failed":
                return (
                    <div className="text-center py-5">
                        <FaTimesCircle className="text-danger mb-4" size={80} />
                        <h2 className="text-danger fw-bold">Payment Failed</h2>
                        <p className="lead mb-4 text-muted">
                            Your transaction could not be completed. If money was deducted, it will be refunded.
                        </p>
                        <div className="d-grid gap-2 d-sm-flex justify-content-sm-center">
                            <Button variant="warning" size="lg" className="fw-bold px-4" onClick={() => navigate("/deposit")}>
                                Try Again
                            </Button>
                            <Button variant="outline-light" size="lg" className="px-4" onClick={() => navigate("/user-dashboard")}>
                                Back Home
                            </Button>
                        </div>
                    </div>
                );
            case "pending":
                return (
                    <div className="text-center py-5">
                        <FaClock className="text-warning mb-4" size={80} />
                        <h2 className="text-warning fw-bold">Payment Pending</h2>
                        <p className="lead mb-4 text-muted">
                            We are waiting for confirmation from the payment gateway. 
                            This usually takes a few minutes.
                        </p>
                        <div className="d-grid gap-2 d-sm-flex justify-content-sm-center">
                            <Button variant="warning" size="lg" className="fw-bold px-4" onClick={() => navigate("/transactions")}>
                                Check History
                            </Button>
                            <Button variant="outline-light" size="lg" className="px-4" onClick={() => navigate("/deposit")}>
                                New Deposit
                            </Button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Container className="mt-5 pt-5 mb-5">
            <Card className="bg-dark text-white border-secondary shadow-lg">
                <Card.Body className="p-5">
                    {renderContent()}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default DepositStatus;
